import React, { useState, useEffect, lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { cartStore } from '../hooks/cartStore';

export const useSharedCart = () => {
  // Get the initial state from the store
  const [cartState, setCartState] = useState(cartStore.getCartState());

  useEffect(() => {
    // Define the listener function
    const handleStoreChange = (newState) => {
      setCartState(newState);
    };

    // Subscribe to the store when the component mounts
    const unsubscribe = cartStore.subscribe(handleStoreChange);

    // Unsubscribe from the store when the component unmounts
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // Return the current state and the store's action methods
  return {
    cartState,
    addItem: cartStore.addItem,
    removeItem: cartStore.removeItem,
    updateQuantity: cartStore.updateQuantity,
    clearCart: cartStore.clearCart,
    fetchCart: cartStore.fetchCart,
    // Add the new actions for MiniCart visibility
    openMiniCart: cartStore.openMiniCart,
    closeMiniCart: cartStore.closeMiniCart,
    isMiniCartOpen: cartStore.isMiniCartOpen,
    openGiftPopup: cartStore.openGiftPopup,
    closeGiftPopup: cartStore.closeGiftPopup,
    isGiftPopupOpen: cartStore.isGiftPopupOpen,
  };
};

const loadVite = () => {
  // Lazily load the components
  const CheckoutButton = lazy(() => import('../components/CheckoutButton/CheckoutButton'));
  const SavingValue = lazy(() => import('../components/SavingValue/SavingValue'));
  const CompareValue = lazy(() => import('../components/CompareValue/CompareValue'));
  const GrandTotalValue = lazy(() => import('../components/GrandTotalValue/GrandTotalValue'));
  const MiniCart = lazy(() => import('../components/MiniCart/MiniCart'));
  const OrderTracking = lazy(() => import('../components/OrderTracking/OrderTracking'));
  const GiftPopup = lazy(() => import('../components/GiftPopup/GiftPopup'));
  const MiniGifts = lazy(() => import('../components/MiniGifts/MiniGifts'));
  const ShippingBar = lazy(() => import('../components/ShippingBar/ShippingBar'));
  const Recommendations = lazy(() => import('../components/Recommendations/Recommendations'));
  const RecentItems = lazy(() => import('../components/RecentItems/RecentItems'));
  const CartContent = lazy(() => import('../components/CartContent/CartContent'));
  const CartCounter = lazy(() => import('../components/CartCounter/CartCounter'));

  // Un componente simple para validar el montaje
  const ViteMountValidation = () => {
    return <div style={{ display: 'none' }} id="swf-vite-validation"></div>;
  };

  // Helper function to mount a component onto an element
  const mountComponent = (element, Component) => {
    // Check if element exists and hasn't been initialized yet
    if (element && !element.__reactRootInitialized) {
      const elementProps = element.dataset;
      const root = ReactDOM.createRoot(element);
      // Mark the element as initialized
      element.__reactRootInitialized = true;
      root.render(
        <React.StrictMode>
          
            {Component.loads === "lazy" ? (
              <Suspense fallback={<div>Loading...</div>}>
                <Component {...elementProps} />
              </Suspense>
            ) : (
              <Component {...elementProps} />
            )}
            
        </React.StrictMode>
      );
      return true; // Indicate mounting occurred
    }
    return false; // Indicate mounting did not occur
  };

  // Define mount points with their corresponding components
  const reactMountPoints = [
    { id: 'swf-vite-mount', Component: ViteMountValidation },
    { className: 'swf-checkout-button', Component: CheckoutButton, loads: "lazy" },
    { className: 'swf-saving-value', Component: SavingValue },
    { className: 'swf-compare-value', Component: CompareValue },
    { className: 'swf-grand-total-value', Component: GrandTotalValue },
    { className: 'swf-cart-counter', Component: CartCounter },
    { id: 'swf-mini-cart-container', Component: MiniCart },
    { id: 'swf-order-tracking', Component: OrderTracking },
    { id: 'swf-gift-popup', Component: GiftPopup },
    { className: 'swf-mini-gifts', Component: MiniGifts },
    { className: 'swf-shipping-bar', Component: ShippingBar },
    { className: 'swf-recommendations', Component: Recommendations, loads: "lazy" },
    { className: 'swf-recent-items', Component: RecentItems, loads: "lazy" },
    { className: 'swf-cart-content', Component: CartContent },
  ];

  let didMount = false; // Keep track if any component was mounted

  reactMountPoints.forEach(({ id, className, Component }) => {
    if (id) {
      // Handle elements selected by ID
      const element = document.getElementById(id);
      if (mountComponent(element, Component)) {
        didMount = true;
      }
    } else if (className) {
      // Handle elements selected by class name
      const elements = document.querySelectorAll(`.${className}`);
      elements.forEach(element => {
        if (mountComponent(element, Component)) {
          didMount = true;
        }
      });
    }
  });
}

window.loadVite = loadVite;
loadVite();

// Optional: Add listeners for Shopify Theme Editor events if needed
// document.addEventListener('shopify:section:load', loadVite);
// document.addEventListener('shopify:section:reorder', loadVite);
// document.addEventListener('shopify:block:select', loadVite);
// etc. (Be mindful of performance if adding many listeners)